import React, { createContext, useContext, useEffect, useState } from 'react';
import { auth } from '../firebase/config';
import { onAuthStateChanged, GoogleAuthProvider, signInWithPopup, signOut, RecaptchaVerifier, signInWithPhoneNumber, PhoneAuthProvider, linkWithCredential } from 'firebase/auth';
import { api } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
    const [currentUser, setCurrentUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dbUser, setDbUser] = useState(null);

    function setupRecaptcha(elementId, useVisibleRecaptcha = false) {
        console.log('[setupRecaptcha] Initializing reCAPTCHA for element:', elementId);

        // Clean up existing verifier
        if (window.recaptchaVerifier) {
            try {
                console.log('[setupRecaptcha] Clearing existing recaptcha verifier');
                window.recaptchaVerifier.clear();
            } catch (e) {
                console.warn("[setupRecaptcha] Failed to clear old recaptcha", e);
            }
            window.recaptchaVerifier = null;
        }

        // Verify DOM element exists
        const element = document.getElementById(elementId);
        if (!element) {
            const error = `reCAPTCHA container element '${elementId}' not found in DOM`;
            console.error('[setupRecaptcha]', error);
            throw new Error(error);
        }

        try {
            const recaptchaConfig = {
                'size': useVisibleRecaptcha ? 'normal' : 'invisible',
                'callback': (response) => {
                    console.log('[setupRecaptcha] reCAPTCHA solved successfully');
                },
                'expired-callback': () => {
                    console.warn('[setupRecaptcha] reCAPTCHA expired');
                },
                'error-callback': (error) => {
                    console.error('[setupRecaptcha] reCAPTCHA error:', error);
                }
            };

            window.recaptchaVerifier = new RecaptchaVerifier(auth, elementId, recaptchaConfig);
            console.log('[setupRecaptcha] reCAPTCHA verifier created successfully');
        } catch (error) {
            console.error('[setupRecaptcha] Failed to create RecaptchaVerifier:', error);
            throw error;
        }
    }

    async function syncWithBackend(firebaseUser) {
        try {
            const idToken = await firebaseUser.getIdToken();
            const res = await api.post('/auth/login', { idToken });
            const { token, user, isNewUser, restaurant } = res.data;
            localStorage.setItem('backendToken', token);
            const userWithFlag = { ...user, isNewUser, restaurant };
            setDbUser(userWithFlag);
            return userWithFlag;
        } catch (error) {
            console.error("Backend Sync Failed", error);
            throw error;
        }
    }

    async function loginWithPhone(phoneNumber, appVerifier) {
        console.log('[loginWithPhone] Attempting to send OTP to:', phoneNumber);
        try {
            if (!appVerifier) {
                throw new Error('reCAPTCHA verifier not initialized');
            }

            const confirmationResult = await signInWithPhoneNumber(auth, phoneNumber, appVerifier);
            console.log('[loginWithPhone] OTP sent successfully');
            return confirmationResult;
        } catch (error) {
            console.error('[loginWithPhone] Phone Auth Error:', {
                code: error.code,
                message: error.message,
                details: error
            });


            // Provide user-friendly error messages
            if (error.code === 'auth/invalid-phone-number') {
                throw new Error('Invalid phone number format. Please use international format (e.g., +1 234 567 8900)');
            } else if (error.code === 'auth/missing-phone-number') {
                throw new Error('Please enter a phone number');
            } else if (error.code === 'auth/quota-exceeded') {
                throw new Error('SMS quota exceeded. Please try again later');
            } else if (error.code === 'auth/captcha-check-failed') {
                throw new Error('reCAPTCHA verification failed. Please refresh the page and try again');
            } else if (error.code === 'auth/invalid-app-credential' || error.message.includes('INVALID_APP_CREDENTIAL')) {
                throw new Error('reCAPTCHA verification failed. Please complete the reCAPTCHA challenge and try again');
            }

            throw error;
        }
    }

    async function verifyOtp(confirmationResult, otp) {
        try {
            const result = await confirmationResult.confirm(otp);
            const backendUser = await syncWithBackend(result.user);
            return backendUser;
        } catch (error) {
            console.error("OTP Verification Error", error);
            throw error;
        }
    }

    async function linkPhoneToUser(currentUser, confirmationResult, otp) {
        try {
            const credential = PhoneAuthProvider.credential(confirmationResult.verificationId, otp);
            const result = await linkWithCredential(currentUser, credential);
            const backendUser = await syncWithBackend(result.user);
            return backendUser;
        } catch (error) {
            console.error("Link Phone Error", error);
            throw error;
        }
    }

    async function loginWithGoogle() {
        const provider = new GoogleAuthProvider();
        try {
            const result = await signInWithPopup(auth, provider);
            const backendUser = await syncWithBackend(result.user);
            return backendUser;
        } catch (error) {
            console.error("Login failed", error);
            throw error;
        }
    }

    function logout() {
        return signOut(auth);
    }

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            setCurrentUser(user);
            if (user) {
                try {
                    await syncWithBackend(user);
                } catch (e) {
                    console.error("Failed to sync user", e);
                }
            } else {
                localStorage.removeItem('backendToken');
                setDbUser(null);
            }
            setLoading(false);
        });

        return unsubscribe;
    }, []);

    const value = {
        currentUser,
        dbUser,
        loginWithGoogle,
        loginWithPhone,
        verifyOtp,
        linkPhoneToUser,
        setupRecaptcha,
        logout
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
};
